import { formatDate } from "@shared/utils/date";
import {
  BACKTESTING_EXTRA_DETAILS_PATH,
  SORT_ORDER_MAPPING,
} from "common/constants";
import React, { CSSProperties, useContext, useMemo } from "react";
import Table from "common/Table/Table";
import { PrecisionConfig, SortingOrder } from "types";
import { ColumnsType } from "antd/es/table";
import { TradeProgressTooltip } from "features/trades/common/TradeProgressTooltip";
import { useSelector } from "react-redux";
import Popover from "common/Popover/Popover";
import { FireIcon } from "common/Icons/Icons";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import NextOrder from "features/bots/MyBots/NextOrder";
import { getTableOnChangeFunction } from "common/Table/utils";
import useMedia from "utils/hooks/useMedia";
import {
  Direction,
  FilterType,
  LanguageNameSpaces,
  TradeData,
} from "@shared/types";
import {
  closedTradesFilters,
  openTradesFilters,
} from "@shared/slices/filters.slice";
import { OrderTypes } from "@shared/features/trades/common/types";
import { typography } from "@shared/common/Themes/typography";
import {
  FIELD_NAME_TO_SORT,
  TradesTableSort,
} from "@shared/features/trades/useTradesSort";
import TradeProgressGraph from "features/trades/common/TradeProgressGraph";
import TradeActionsDropdown from "features/trades/OpenTrades/TradeActionsDropdown";
import { TableRowSelection } from "antd/es/table/interface";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useTheme } from "styled-components";
import { TableLoadingEmpty } from "common/styles";
import GridProgressGraph from "features/trades/common/GridProgressGraph";
import { MARGIN_EXCHANGES } from "@shared/common/constants";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import ShareTradeButton from "features/trades/common/ShareTradeButton";
import PnlTag from "features/trades/common/PnlTag";
import { useRouteMatch } from "react-router-dom";
import { openMessage } from "utils/misc";
import useSocketPnl from "@shared/services/socket/useSocketPnl";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import useMeasure from "utils/hooks/useMeasure.ts";
import NewLookProgressCell from "features/trades/NewLook/NewLookProgressCell.tsx";
import {
  FigureCell,
  NextOrderCell,
  PnlCell,
  SymbolCell,
  TimeCell,
  TradeRowsSkeleton,
} from "features/trades/NewLook/NewLookTradeCells.tsx";
import {
  NEW_LOOK_COMPACT_TABLE_CLASS,
  NEW_LOOK_PROGRESS_COLUMN_CLASS,
} from "features/trades/common/constants";

const PROGRESS_COLUMN_KEY = "progress";
const NEW_LOOK_OPEN_PROGRESS_MIN = 968;

interface TradesTableProps {
  data: TradeData[];
  allowTradeClick?: boolean;
  getTradeAmount: (tradeData: TradeData) => React.JSX.Element;
  getTradeQuantity: (tradeData: TradeData) => React.JSX.Element;
  isOpenTrades: boolean;
  openTradeExtraDetails: (tradeData: TradeData) => void;
  precisionConfig?: PrecisionConfig;
  loading: boolean;
  amountChangeHeader: "btc_amount_change" | "usd_amount_change";
  tableRowProps?: TableRowSelection<TradeData>;
  handlePaginationChange: (pageNumber: number, pageSize: number) => void;
  pagination?: { pageNumber: number; pageSize: number };
  hideActions?: boolean;
  sortData: {
    sortBy: TradesTableSort;
    setSortBy: (sortBy: TradesTableSort) => void;
    sortDirection: SortingOrder;
    setSortDirection: (sortDirection: SortingOrder) => void;
  };
  emptyState?: React.JSX.Element;
}

const TradesTable = ({
  data,
  getTradeAmount,
  getTradeQuantity,
  isOpenTrades,
  precisionConfig,
  openTradeExtraDetails,
  loading,
  amountChangeHeader,
  tableRowProps,
  handlePaginationChange,
  pagination,
  hideActions,
  sortData,
  emptyState,
  allowTradeClick = true,
}: TradesTableProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const appTheme = useTheme();
  const { isNewLook } = appTheme;
  const hideTimeColumnByScreen = useMedia(
    [isOpenTrades ? `(max-width: 1400px)` : `(max-width: 1000px)`],
    [true],
    false
  );
  // New look: the time column is the first to go when the table itself is
  // too narrow for every column (the progress caption keeps the time in
  // trade), so the actions never scroll out of view.
  const [measureTableRef, { width: tableWidth }] = useMeasure<HTMLDivElement>();
  const hideOpenedAtColumn = isNewLook
    ? !!tableWidth &&
      tableWidth <
        (isOpenTrades ? NEW_LOOK_OPEN_TIME_MIN : NEW_LOOK_CLOSED_TIME_MIN)
    : hideTimeColumnByScreen;
  const isCompactTable = isNewLook && hideOpenedAtColumn;
  const hideProgressColumn =
    isNewLook &&
    isOpenTrades &&
    !!tableWidth &&
    tableWidth < NEW_LOOK_OPEN_PROGRESS_MIN;
  const compactTitle = (title: string, shortTitle: string) =>
    isCompactTable ? <span title={title}>{shortTitle}</span> : title;
  const splitAmountQuantity = useMedia([`(max-width: 1250px)`], [true], false);
  const filterSelectors = isOpenTrades
    ? openTradesFilters.selectors
    : closedTradesFilters.selectors;
  const filtersData = useSelector(filterSelectors.selectFiltersData);
  const exchangesFilter = filtersData[FilterType.EXCHANGES];
  const hasLeverageExchangeFilter = exchangesFilter?.options?.some(
    (selectedExchange) =>
      [...MARGIN_EXCHANGES].some(
        (leverageExchange) => leverageExchange === selectedExchange
      )
  );
  const { sortBy, setSortBy, sortDirection, setSortDirection } = sortData;
  const sortOrder = SORT_ORDER_MAPPING[sortDirection];
  const { getInvalidAccountData } = useInvalidAccount();
  const isBacktesting = !!useRouteMatch(BACKTESTING_EXTRA_DETAILS_PATH);
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isReadOnly } = useSubUserPermissions();
  const shareInActionsMenu = isCompactTable && isOpenTrades && !isReadOnly;

  const timeDataIndex = isOpenTrades ? "open_time" : "close_time";

  const getTradeSymbolColumn = (trade: TradeData) => {
    const invalidAccountData = getInvalidAccountData(trade.client_id);
    if (isNewLook) {
      return (
        <SymbolCell
          trade={trade}
          isHot={
            !invalidAccountData.isInvalidOrInactive &&
            !isBacktesting &&
            !!trade.closest_order?.is_hot
          }
        />
      );
    }
    return (
      <Flex $justifyContent="flex-start">
        <IconWithSource
          symbol={trade.symbol}
          sourceType={trade.source_type}
          source={trade.group}
        />
        <div style={{ maxWidth: 200, overflow: "hidden" }}>
          <InactiveAccountIcon
            accountId={trade.client_id}
            objectType={AccountObjectType.TRADE}
          >
            <Text
              style={{
                display: "flex",
                alignItems: "center",
                color: "inherit",
              }}
            >
              {trade.symbol}
              {!invalidAccountData.isInvalidOrInactive &&
                !isBacktesting &&
                trade.closest_order?.is_hot && (
                  <FireIcon
                    style={{
                      marginLeft: 6,
                      fontSize: appTheme.typography.fontSizes.M,
                    }}
                  />
                )}
            </Text>
          </InactiveAccountIcon>
          <InactiveAccountIcon
            accountId={trade.client_id}
            objectType={AccountObjectType.ACCOUNT}
            hideIcon={true}
          >
            <Text
              type="secondary"
              $ellipsis={true}
              style={{
                color: "inherit",
                maxWidth: 140,
              }}
            >
              {trade.account}
            </Text>
          </InactiveAccountIcon>
        </div>
      </Flex>
    );
  };

  let columns: ColumnsType<TradeData> = [
    {
      title: t("table.headers.symbol"),
      dataIndex: "symbol",
      showSorterTooltip: false,
      sortOrder: sortBy === TradesTableSort.SYMBOL ? sortOrder : undefined,
      sorter: true,
      render: (_, trade) => getTradeSymbolColumn(trade),
    },
    {
      title: isCompactTable ? (
        <span
          title={`${t("table.headers.amount")} (${t(
            "table.headers.qty_abbr"
          )})`}
        >
          {t("table.headers.amount")}
        </span>
      ) : (
        <>
          {t("table.headers.amount")}{" "}
          <span style={{ fontSize: typography.fontSizes.S }}>
            ({t("table.headers.qty_abbr")})
          </span>
        </>
      ),
      dataIndex: "amount",
      showSorterTooltip: false,
      sortOrder: sortBy === TradesTableSort.AMOUNT ? sortOrder : undefined,
      sorter: true,
      ...(isNewLook && NEW_LOOK_END_COLUMN),
      render: (_, tradeData) => {
        if (isNewLook) {
          return (
            <FigureCell
              value={getTradeAmount(tradeData)}
              meta={getTradeQuantity(tradeData)}
            />
          );
        }
        let quantityStyle: CSSProperties = {
          marginLeft: 0,
        };
        if (!splitAmountQuantity) {
          quantityStyle = {
            position: "relative",
            marginLeft: 6,
            top: 1,
          };
        }
        return (
          <Flex
            $justifyContent="flex-start"
            $alignItems={splitAmountQuantity ? "flex-start" : "center"}
            $flexDirection={splitAmountQuantity ? "column" : "row"}
          >
            {getTradeAmount(tradeData)}
            <Text
              style={quantityStyle}
              type="secondary"
              $size={typography.fontSizes.S}
            >
              ({getTradeQuantity(tradeData)})
            </Text>
          </Flex>
        );
      },
    },
    {
      title: isOpenTrades
        ? t("table.headers.opened_at")
        : t("table.headers.closed_at"),
      dataIndex: timeDataIndex,
      key: TIMESTAMP_COLUMN_KEY,
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sortOrder:
        sortBy ===
        (isOpenTrades ? TradesTableSort.OPEN_TIME : TradesTableSort.CLOSE_TIME)
          ? sortOrder
          : undefined,
      sorter: true,
      render: (_, tradeData) => {
        const timestamp = tradeData.timestamp;
        const timeDisplay = formatDate(timestamp, true, true, isOpenTrades);
        const time = isNewLook ? (
          <TimeCell>{timeDisplay}</TimeCell>
        ) : (
          timeDisplay
        );
        return isOpenTrades ? (
          <Popover
            content={formatDate(timestamp, true, true)}
            styles={{ root: { maxWidth: 400 } }}
          >
            {time}
          </Popover>
        ) : (
          time
        );
      },
    },
    {
      title: t("table.headers.pnl"),
      dataIndex: "change",
      showSorterTooltip: false,
      sortOrder: sortBy === TradesTableSort.CHANGE ? sortOrder : undefined,
      sorter: true,
      ...(isNewLook && NEW_LOOK_END_COLUMN),
      render: (_, tradeData) => {
        if (isNewLook) {
          return (
            <PnlCell
              trade={tradeData}
              socketPnlData={socketPnlValue}
              amountChangeHeader={amountChangeHeader}
            />
          );
        }
        return (
          <PnlTag
            socketPnlData={socketPnlValue}
            tradeData={tradeData}
            amountChangeHeader={amountChangeHeader}
            precisionConfig={precisionConfig}
          />
        );
      },
    },
    {
      title: (
        <div style={{ display: "flex" }}>
          {t("table.headers.progress")}
          <TradeProgressTooltip isOpenTrades={isOpenTrades} />
        </div>
      ),
      dataIndex: "progress",
      key: PROGRESS_COLUMN_KEY,
      className: NEW_LOOK_PROGRESS_COLUMN_CLASS,
      showSorterTooltip: false,
      sortOrder: sortBy === TradesTableSort.PROGRESS ? sortOrder : undefined,
      sorter: true,
      render: (_, tradeData) => {
        if (isNewLook) {
          return (
            <NewLookProgressCell
              tradeData={tradeData}
              liveData={socketPnlValue?.[tradeData.id]}
              isOpenTrades={isOpenTrades}
            />
          );
        }
        const graphData = (socketPnlValue?.[tradeData.id] || tradeData)
          ?.graph_data;
        if ("current_price" in graphData) {
          return <GridProgressGraph tradeData={tradeData} />;
        } else {
          return (
            <TradeProgressGraph graphData={graphData} {...TRADE_GRAPH_PROPS} />
          );
        }
      },
    },
  ];

  const addDynamicColumns = () => {
    if (!isDrawer) {
      if (!hideActions) {
        columns.push({
          title: "",
          dataIndex: "id",
          key: "action",
          width: 40,
          render: (_, tradeData) => (
            <Flex
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              style={{ gridGap: isNewLook ? 2 : 6 }}
            >
              {!shareInActionsMenu && (
                <ShareTradeButton tradeData={tradeData} />
              )}
              {isOpenTrades && !isReadOnly && (
                <TradeActionsDropdown
                  tradeData={tradeData}
                  isOpenTrades={isOpenTrades}
                  isCardsView={shareInActionsMenu}
                />
              )}
            </Flex>
          ),
        });
      }
      if (isOpenTrades) {
        columns.splice(
          2,
          0,
          {
            title: compactTitle(
              t("table.headers.next_entry_or_stop"),
              t("table.headers.next_entry_or_stop_short")
            ),
            dataIndex: "next_entry_or_stop",
            showSorterTooltip: false,
            sortOrder:
              sortBy === TradesTableSort.NEXT_ENTRY_OR_STOP
                ? sortOrder
                : undefined,
            sorter: true,
            ...(isNewLook && NEW_LOOK_END_COLUMN),
            render: (_, tradeData) =>
              isNewLook ? (
                <NextOrderCell
                  trade={tradeData}
                  nextOrder={tradeData.next_entry || tradeData.next_stop}
                  orderType={
                    tradeData.next_entry ? OrderTypes.ENTRY : OrderTypes.STOP
                  }
                />
              ) : (
                <NextOrder
                  symbol={tradeData.symbol}
                  exchange={tradeData.exchange}
                  nextOrder={tradeData.next_entry || tradeData.next_stop}
                  orderType={
                    tradeData.next_entry ? OrderTypes.ENTRY : OrderTypes.STOP
                  }
                  isMobile={true}
                  isPriceSecondary={true}
                  style={NEXT_ORDER_STYLE}
                />
              ),
          },
          {
            title: compactTitle(
              t("table.headers.next_take_profit"),
              t("table.headers.next_take_profit_short")
            ),
            dataIndex: "next_take_profit",
            showSorterTooltip: false,
            sortOrder:
              sortBy === TradesTableSort.NEXT_TAKE_PROFIT
                ? sortOrder
                : undefined,
            sorter: true,
            ...(isNewLook && NEW_LOOK_END_COLUMN),
            render: (_, tradeData) => {
              if (isNewLook) {
                return (
                  <NextOrderCell
                    trade={tradeData}
                    nextOrder={tradeData.next_take_profit}
                    orderType={OrderTypes.TAKE_PROFIT}
                  />
                );
              }
              return (
                <NextOrder
                  symbol={tradeData.symbol}
                  exchange={tradeData.exchange}
                  nextOrder={tradeData.next_take_profit}
                  orderType={OrderTypes.TAKE_PROFIT}
                  isMobile={true}
                  isPriceSecondary={true}
                  style={NEXT_ORDER_STYLE}
                />
              );
            },
          }
        );
      }
    }

    const hasShortPosition = data.some(
      (trade) => trade["Long/Short"] === Direction.SHORT
    );
    const hasLeverageExchange = data.some((trade) =>
      MARGIN_EXCHANGES.has(trade.exchange)
    );
    // New look: side and leverage read on the symbol's line, as on the
    // dashboard, instead of a column of pills.
    if (
      !isNewLook &&
      (hasShortPosition || hasLeverageExchange || hasLeverageExchangeFilter)
    ) {
      columns.splice(2, 0, {
        title: t("table.headers.direction"),
        dataIndex: "leverage",
        key: "leverage",
        showSorterTooltip: false,
        sortOrder: sortBy === TradesTableSort.LEVERAGE ? sortOrder : undefined,
        sorter: true,
        render: (leverage, tradeData) => {
          const marginType = tradeData.margin_type
            ? `${tradeData.margin_type} `
            : "";
          const leverageDisplay = leverage ? `${leverage}X` : "";
          const margin = `${marginType}${leverageDisplay}`;
          const longShort = tradeData["Long/Short"];
          return (
            <>
              <Text>{longShort}</Text>
              <Text type="secondary" $size={typography.fontSizes.S}>
                {margin}
              </Text>
            </>
          );
        },
      });
    }
  };
  addDynamicColumns();
  if (hideOpenedAtColumn) {
    columns = columns.filter((column) => column.key !== TIMESTAMP_COLUMN_KEY);
  }
  if (hideProgressColumn) {
    columns = columns.filter((column) => column.key !== PROGRESS_COLUMN_KEY);
  }

  const handleChange = getTableOnChangeFunction<TradesTableSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT
  );

  const visibleTrades = useMemo(() => {
    if (pagination) {
      const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      return data.slice(startIndex, endIndex);
    }
  }, [data, pagination]);

  const { socketPnlValue } = useSocketPnl(
    isBacktesting ? undefined : visibleTrades
  );

  const onRowClick = (tradeData: TradeData) => {
    if (allowTradeClick) openTradeExtraDetails(tradeData);
    else if (isBacktesting) {
      openMessage(t("warnings.backtest_pending"), "warning");
    }
  };

  const showSkeleton = isNewLook && !isDrawer && loading && !data.length;
  let emptyText: React.ReactNode = emptyState || <TableLoadingEmpty />;
  if (showSkeleton) emptyText = <TradeRowsSkeleton />;

  return (
    <LoadingSpinnerOverlay
      isLoading={loading && !showSkeleton}
      spinnerMarginTop={140}
      backgroundColor={
        appTheme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"]
      }
      zIndex={2}
    >
      <div
        ref={measureTableRef}
        className={isCompactTable ? NEW_LOOK_COMPACT_TABLE_CLASS : undefined}
      >
        <Table<TradeData>
          key={isOpenTrades ? "open-trades" : "closed-trades"}
          columns={columns}
          dataSource={data}
          rowClassName={`table-half-padding${
            allowTradeClick ? " u-cursorPointer" : ""
          }`}
          rowKey={(tradeData) => tradeData.id}
          onRow={(tradeData) => ({
            onClick: () => onRowClick(tradeData),
            // New look: rows that open the drawer are reachable by keyboard.
            ...(isNewLook &&
              allowTradeClick && {
                tabIndex: 0,
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (
                    event.key === "Enter" &&
                    event.target === event.currentTarget
                  )
                    onRowClick(tradeData);
                },
              }),
          })}
          onChange={handleChange}
          onPaginationChange={handlePaginationChange}
          rowSelection={isReadOnly ? undefined : tableRowProps}
          locale={{ emptyText }}
          pagination={{ showSizeChanger: !isDrawer }}
        />
      </div>
    </LoadingSpinnerOverlay>
  );
};

export default TradesTable;

const TRADE_GRAPH_PROPS = {
  size: "s",
  style: { justifyContent: "flex-start", margin: 0 },
} as const;
const TIMESTAMP_COLUMN_KEY = "timestamp";

const NEXT_ORDER_STYLE = { alignItems: "flex-start" };
// New look: figure columns are right-aligned, titles over their figures.
const NEW_LOOK_END_COLUMN = { align: "right", className: "nl-end" } as const;
// Table widths that fit every column including the opened/closed time.
const NEW_LOOK_OPEN_TIME_MIN = 1300;
const NEW_LOOK_CLOSED_TIME_MIN = 760;
